// The client side of /api/sources (BUILD-BRIEF-R6 §5.1) — the typed reads and the three verbs
// the Sources page offers: enable/disable + edit settings (PATCH), refresh one source (POST
// .../refresh), and file the register's own propose_organisation by hand for an entry the
// register did not resolve (POST .../entries/:id/propose). Routes and their exact response
// shapes are `routes/sources.ts`'s (a sibling package); this file only names them.
import { qs, request } from './client.js';
import type {
  RefreshOutcome, RefreshReport, Source, SourceDocument, SourceEntry, SourceEntryResolution,
  SourceEntryStatus, SourceRefresh,
} from '../../shared/sources.v1.js';

export interface ListResponse<T> { items: T[]; total: number }

/** What `POST /entries/:id/propose` answers beside the updated entry: the proposal row it wrote
 *  (`{ entry, proposal }` — the route's own shape). Only its ref is read here. */
export interface ProposalStub { ref: string; kind: string; status: string }

/**
 * One `sources` row plus the counts `GET /api/sources` folds in per §5.1 ("+ counts per source,
 * one query with lateral subselects") — the exact aggregate columns are the routes package's to
 * name; this is this package's own reading of what the Sources page's rail and table need, kept
 * here rather than guessed inline at every call site. A field the route does not yet send comes
 * back `undefined`, and every reader below renders that as "not collected", never as zero.
 */
export interface SourceListRow extends Source {
  documents_current: number;
  documents_superseded: number;
  entries_current: number;
  entries_resolved: number;
  entries_waiting: number;
  links_held: number;
  links_waiting: number;
  series_points: number;
  series_count: number;
  last_refresh_outcome: RefreshOutcome | null;
  last_refresh_error: string | null;
}

/** One row per source, with the counts the rail and the table need — a lateral subselect each,
 *  per the route's own plan (§5.1). `q` is unused today but kept for a future filter band. */
export function listSources(q?: string): Promise<ListResponse<SourceListRow>> {
  return request(`/sources${qs({ q })}`);
}

export function getSource(key: string): Promise<Source> {
  return request(`/sources/${encodeURIComponent(key)}`);
}

export function listDocuments(key?: string): Promise<ListResponse<SourceDocument>> {
  return request(key ? `/sources/${encodeURIComponent(key)}/documents` : '/sources/documents');
}

export function listRefreshes(key?: string, limit?: number): Promise<ListResponse<SourceRefresh>> {
  return request(key
    ? `/sources/${encodeURIComponent(key)}/refreshes${qs({ limit })}`
    : `/sources/refreshes${qs({ limit })}`);
}

export interface ListEntriesFilters {
  key?: string;
  resolution?: SourceEntryResolution;
  organisation_id?: number;
  /** Observed presence, not a lifecycle (§8.3 #3): `gone` is a name a new vintage stopped
   *  printing. Omitted, the route answers both. */
  status?: SourceEntryStatus;
}

export function listEntries(filters: ListEntriesFilters = {}): Promise<ListResponse<SourceEntry>> {
  return request(`/sources/entries${qs({
    key: filters.key, resolution: filters.resolution, organisation_id: filters.organisation_id,
    status: filters.status,
  })}`);
}

/** `{ refresh }` per §5.1 — the refresh row this call produced (or is still running as). */
export function refreshSource(key: string): Promise<{ refresh: SourceRefresh }> {
  return request(`/sources/${encodeURIComponent(key)}/refresh`, { method: 'POST' });
}

/** The only way `enabled` is ever set; `settings` is validated server-side against the module's
 *  own schema (an undeclared key, or a source with none, answers 400 naming what it takes). */
export function updateSource(key: string, patch: { enabled?: boolean; settings?: Record<string, unknown> }): Promise<Source> {
  return request(`/sources/${encodeURIComponent(key)}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

/** Files `propose_organisation` by hand for one unresolved/ambiguous entry — the console's own
 *  path (§5.1: `invoke` under the operator's own principal is refused by the door, so the route
 *  calls `createProposal` directly, as `routes/network.ts#POST /links` already does). */
export function proposeEntry(id: number): Promise<{ entry: SourceEntry; proposal: ProposalStub }> {
  return request(`/sources/entries/${id}/propose`, { method: 'POST' });
}

export type { RefreshReport };
